import { abs, clamp, dot, float, length, max, normalize, smoothstep, step, vec3 } from 'three/tsl';
import { Vector3 } from 'three/webgpu';

/**
 * A cave mouth in a terrain hollow. The ground settles into a flat plateau at
 * `height` around it, a sphere of radius R − height. Near the hole the plateau rolls
 * over into it (the lip, see lipDepthAt). The tunnel's straight entry cylinder is cut
 * out of the terrain and its collision, the tunnel is clipped at the ground, and
 * vegetation keeps clear of the hole.
 */
export interface TerrainMouth {
  /** Unit direction from the hollow centre to the plateau centre (hollow frame). */
  dir: Vector3;
  /** Plateau height, metres above sea level. */
  height: number;
  /** Radius of the flat plateau, and the width of the blend back to natural ground (m). */
  flat: number;
  blend: number;
  /** Tunnel entry cylinder: a point on its axis (hollow-local), the axis (into the rock), radius. */
  point: Vector3;
  axis: Vector3;
  radius: number;
  /** The lip: how far the ground dips at the hole's edge, and how far out it eases back (m). */
  lipDepth: number;
  lipWidth: number;
}

/** Distance along the axis within which the entry cylinder is cut (m). */
export const MOUTH_CUT_LENGTH = 40;
/**
 * The ground around a hole is worn bare: the palette is told it slopes this much (0 = flat,
 * 1 = a wall), so each world paints it with its own bare earth. The tunnel's first metres use
 * the same palette (caveMaterial), so the ground runs on into the cave. (0.34 made it each
 * palette's cliff rock, near black against snow: a stain round every mouth.)
 */
export const WORN_SLOPE = 0.2;
/**
 * The ground darkens toward a hole, to this share of its light at the edge (the walls of the
 * hole hide some of the sky). The tunnel's first rock starts from the same share.
 */
export const RIM_SHADE = 0.8;
/**
 * The lip's share around the hole, by q = (across · down) / ρ: how directly lowering the
 * ground moves it away from the tunnel's axis. Full on the approach side (q ≥ 0.45) and
 * none by the sides and over the roof (q ≤ 0.05). Where q ≤ 0 lowering the ground would
 * move it toward the tunnel, and could sink it into the roof and open a gap; where q > 0 it
 * never can (ρ only grows going down).
 */
const SIDE_LO = 0.05;
const SIDE_HI = 0.45;

const _w = new Vector3();

/**
 * How far below its plateau (a sphere of radius `plateauR`) the ground around a mouth
 * lies, along the outward unit direction `down` (hollow frame). On the approach side the
 * plateau rolls down into the hole: `lipDepth` deep at the edge, easing back as D·(1 − s)²
 * over `lipWidth` (s by distance from the axis), so the way in is a smooth ramp. By the
 * sides and over the roof it stays level (see SIDE_LO).
 *
 * Everything that meets the ground at a mouth uses this (or lipDepthNode on the GPU): the
 * terrain, the tunnel's clip, the collision collar and the boulders.
 */
export function lipDepthAt(m: TerrainMouth, plateauR: number, down: Vector3): number {
  const w = _w.copy(down).multiplyScalar(plateauR).sub(m.point);
  const along = w.dot(m.axis);
  if (Math.abs(along) >= MOUTH_CUT_LENGTH) return 0;
  const across = w.addScaledVector(m.axis, -along);
  const rho = Math.max(across.length(), 1e-6);
  const s = Math.min(1, Math.max(0, (rho - m.radius) / m.lipWidth));
  const t = Math.min(1, Math.max(0, (across.dot(down) / rho - SIDE_LO) / (SIDE_HI - SIDE_LO)));
  return m.lipDepth * (1 - s) * (1 - s) * t * t * (3 - 2 * t);
}

const _x = new Vector3();

/**
 * Where the ground (the plateau and its lip) crosses the line through `p0` (hollow-local,
 * near the mouth) parallel to the mouth's axis: the t of p0 + axis·t. For the collision
 * collar and the boulders, which ring the hole along such lines.
 */
export function groundAlongAxis(m: TerrainMouth, plateauR: number, p0: Vector3): number {
  const b = p0.dot(m.axis);
  const cross = (r: number) => {
    const disc = b * b - (p0.lengthSq() - r * r);
    if (disc < 0) return Number.NaN;
    const s = Math.sqrt(disc);
    return Math.abs(-b + s) < Math.abs(-b - s) ? -b + s : -b - s;
  };
  // The ground lies between the plateau and the lip's full depth; bisect between them.
  let lo = cross(plateauR);
  let hi = cross(plateauR + m.lipDepth + 0.01);
  if (!Number.isFinite(lo)) return 0;
  if (!Number.isFinite(hi)) return lo;
  const above = (t: number) => {
    _x.copy(p0).addScaledVector(m.axis, t);
    const r = _x.length();
    return r - plateauR - lipDepthAt(m, plateauR, _x.divideScalar(r)) <= 0;
  };
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    if (above(mid)) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Distance of a hollow-local point from the mouth's entry axis, or Infinity away from it. */
export function mouthAxisDistance(m: TerrainMouth, local: Vector3): number {
  _w.subVectors(local, m.point);
  const t = _w.dot(m.axis);
  if (Math.abs(t) > MOUTH_CUT_LENGTH) return Number.POSITIVE_INFINITY;
  return _w.addScaledVector(m.axis, -t).length();
}

type N = any;

/** lipDepthAt on the GPU. `down` is a unit vec3 node (hollow frame). */
export function lipDepthNode(m: TerrainMouth, plateauR: number, down: N): N {
  const ax = vec3(m.axis.x, m.axis.y, m.axis.z);
  const w = down.mul(plateauR).sub(vec3(m.point.x, m.point.y, m.point.z));
  const along = dot(w, ax);
  const across = w.sub(ax.mul(along));
  const rho = max(length(across), 1e-6);
  const s = clamp(rho.sub(m.radius).div(m.lipWidth), 0, 1);
  const side = smoothstep(SIDE_LO, SIDE_HI, dot(across, down).div(rho));
  const here = float(1).sub(step(MOUTH_CUT_LENGTH, abs(along)));
  return float(1).sub(s).mul(float(1).sub(s)).mul(side).mul(here.mul(m.lipDepth));
}

/**
 * GPU: 0 within `inner` metres of a mouth's entry cylinder, rising to 1 at `outer`
 * metres (vegetation uses it to keep clear of the hole). `lip` adds that share of
 * each mouth's lip width to both. `local` is hollow-local. With a `path` length, the
 * way in is kept clear too: a strip that long (PATH_WIDTH wide) out from the mouth on
 * the side you walk in from, where a cave world puts you at the start, looking in.
 */
export function mouthClearance(
  local: N,
  mouths: TerrainMouth[],
  inner: number,
  outer: number,
  lip = 0,
  path = 0,
): N {
  let clear: N = float(1);
  for (const m of mouths) {
    const extra = lip * m.lipWidth;
    const w = local.sub(vec3(m.point.x, m.point.y, m.point.z));
    const ax = vec3(m.axis.x, m.axis.y, m.axis.z);
    const t = dot(w, ax);
    const perp = length(w.sub(ax.mul(t)));
    const away = step(MOUTH_CUT_LENGTH, abs(t));
    clear = clear.mul(max(smoothstep(m.radius + inner + extra, m.radius + outer + extra, perp), away));
    if (path > 0) {
      // Along the ground away from the mouth on the open side (the stair's way in).
      const d = vec3(m.dir.x, m.dir.y, m.dir.z);
      const out = normalize(ax.sub(d.mul(dot(ax, d)))).negate();
      const along = dot(w, out);
      const lateral = length(w.sub(out.mul(along)).sub(d.mul(dot(w, d))));
      const onPath = step(0, along)
        .mul(float(1).sub(smoothstep(path * 0.8, path, along)))
        .mul(float(1).sub(smoothstep(PATH_WIDTH * 0.5, PATH_WIDTH, lateral)));
      clear = clear.mul(float(1).sub(onPath));
    }
  }
  return clear;
}

/** How wide the way in to a cave mouth is kept clear of trees (m; see mouthClearance). */
const PATH_WIDTH = 7;
